(() => {
  if (window.BSBProductHeroLoaded) return;
  window.BSBProductHeroLoaded = true;

  const SELECTOR = '[data-bsb-product-hero]';
  const INGREDIENT_MIN_WIDTH = 390;
  const INGREDIENT_MAX_WIDTH = 1440;
  const INGREDIENT_BASE_SCALE = 2;
  const INGREDIENT_DESKTOP_SCALE = 2.55;
  const INGREDIENT_BASE_ROTATION = -13;
  const INGREDIENT_DESKTOP_ROTATION = -40;
  const SWIPE_MIN_DISTANCE = 36;
  const SWIPE_AXIS_RATIO = 1.15;
  // Wheel tuning: exactly one slide per gesture, ignoring trackpad inertia, while still allowing quick consecutive gestures.
  const WHEEL_THRESHOLD = 24;
  const WHEEL_MIN_PEAK = 8;
  const WHEEL_GESTURE_GAP = 160;
  const WHEEL_PUSH_MIN_INTERVAL = 300;
  const WHEEL_SMOOTHING = 0.35;
  // Stretches the whole slide transition; 1.5 makes it last 50% longer than the base timings below.
  const TRANSITION_STRETCH = 1.5;
  // Leaving the viewport while exploring returns the section to its passive state.
  const EXIT_RATIO = 0.45;
  const PARALLAX_DURATION = 1.1;
  const DETAILS_DURATION = 0.45;

  class BSBProductHero {
    constructor(root) {
      this.root = root;
      this.gsap = window.gsap;
      this.products = [...root.querySelectorAll('[data-bsb-product]')].map((node) => ({ ...node.dataset }));
      this.currentIndex = 0;
      this.isAnimating = false;
      this.isActive = false;
      this.timeline = null;
      this.controller = new AbortController();
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.animationSpeed = Math.max(0.5, Math.min(1.5, Number(root.dataset.animationSpeed) || 1));
      this.ingredientScaleFactor = Math.max(0.25, (Number(root.dataset.ingredientScale) || 100) / 100);
      this.labels = {
        showDetails: root.dataset.labelShowDetails || 'Show details',
        hideDetails: root.dataset.labelHideDetails || 'Hide details',
        indicator: root.dataset.labelIndicator || 'Product [current] of [total]'
      };
      this.touchStart = null;
      this.wheel = { total: 0, peak: 0, lastEventAt: -Infinity, lastNavigationAt: -Infinity, lastMagnitude: 0, locked: false, smooth: 0, inertiaPeak: 0, inertiaTrough: 0, rising: 0 };
      this.preloadCache = new Map();
      this.resizeFrame = null;
      this.ingredientScale = INGREDIENT_BASE_SCALE;
      this.ingredientRotation = INGREDIENT_BASE_ROTATION;

      this.overlay = root.querySelector('.bsb-hero__overlay');
      this.closeButton = root.querySelector('.bsb-hero__close');
      this.backgroundStack = root.querySelector('.bsb-hero__background-stack');
      this.parallaxAmount = Math.max(0, Math.min(5, Number(root.dataset.backgroundParallax) || 0)) / 100;
      this.parallaxQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.currentBackground = root.querySelector('.bsb-hero__background--current');
      this.incomingBackground = root.querySelector('.bsb-hero__background--incoming');
      this.currentIngredient = root.querySelector('.bsb-hero__ingredients--current');
      this.incomingIngredient = root.querySelector('.bsb-hero__ingredients--incoming');
      this.ingredientMotionLayers = [...root.querySelectorAll('.bsb-hero__ingredient-motion')];
      this.currentBottle = root.querySelector('.bsb-hero__bottle--current');
      this.incomingBottle = root.querySelector('.bsb-hero__bottle--incoming');
      this.lightSweep = root.querySelector('.bsb-hero__light-sweep');
      this.lightSweepBand = root.querySelector('.bsb-hero__light-sweep-band');
      this.copy = root.querySelector('.bsb-hero__copy');
      this.title = root.querySelector('.bsb-hero__title');
      this.tagline = root.querySelector('.bsb-hero__tagline');
      this.description = root.querySelector('.bsb-hero__description');
      this.profile = root.querySelector('.bsb-hero__profile');
      this.mood = root.querySelector('.bsb-hero__mood');
      this.moment = root.querySelector('.bsb-hero__moment');
      this.detailsToggle = root.querySelector('.bsb-hero__details-toggle');
      this.shopButton = root.querySelector('.bsb-hero__shop-button');
      this.indicator = root.querySelector('.bsb-hero__indicator');
      this.indicatorDots = [...root.querySelectorAll('[data-bsb-indicator-dot]')];

      this.updateResponsiveIngredientMetrics();
      this.bindEvents();
      this.observeViewport();
      this.observeCopyHeight();
      this.setSweepMask(this.currentBottle?.currentSrc || this.currentBottle?.src);
      this.startAmbientMotion();
      this.setupParallax();
      this.preloadAdjacent(this.currentIndex);
      this.root.dataset.bsbHeroReady = 'true';
    }

    get lastIndex() {
      return this.products.length - 1;
    }

    bindEvents() {
      const options = { signal: this.controller.signal };
      this.overlay?.addEventListener('click', () => this.activate(), options);
      this.closeButton?.addEventListener('click', () => this.deactivate({ restoreFocus: true }), options);
      this.indicatorDots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index, index > this.currentIndex ? 1 : -1), options);
      });
      this.detailsToggle?.addEventListener('click', () => this.toggleDetails(), options);
      this.root.addEventListener('keydown', (event) => this.handleKeydown(event), options);
      this.root.addEventListener('wheel', (event) => this.handleWheel(event), { ...options, passive: false });
      this.root.addEventListener('touchstart', (event) => this.handleTouchStart(event), { ...options, passive: true });
      this.root.addEventListener('touchend', (event) => this.handleTouchEnd(event), { ...options, passive: true });
      this.root.addEventListener('touchcancel', () => { this.touchStart = null; }, options);
      this.root.addEventListener('shopify:block:select', (event) => this.handleBlockSelect(event), options);
      this.motionQuery.addEventListener?.('change', (event) => this.handleMotionPreference(event), options);
      window.addEventListener('resize', () => this.scheduleResponsiveIngredientUpdate(), options);
    }

    observeViewport() {
      if (!('IntersectionObserver' in window)) return;
      this.viewportObserver = new IntersectionObserver(([entry]) => {
        if (this.isActive && entry.intersectionRatio < EXIT_RATIO) this.deactivate();
      }, { threshold: [0, EXIT_RATIO, 1] });
      this.viewportObserver.observe(this.root);
    }

    // On mobile the overlay label rests just above the card, whose height changes with each fragrance.
    observeCopyHeight() {
      if (!this.copy || !('ResizeObserver' in window)) return;
      this.copyObserver = new ResizeObserver(() => {
        const height = this.copy.offsetHeight;
        this.root.style.setProperty('--bsb-copy-height', `${height}px`);
        // The bottle keeps clear of the tallest closed card, so it doesn't resize per fragrance or when details open.
        if (this.copy.dataset.detailsOpen !== 'true' && height > (this.copyRestHeight || 0)) {
          this.copyRestHeight = height;
          this.root.style.setProperty('--bsb-copy-rest-height', `${height}px`);
        }
      });
      this.copyObserver.observe(this.copy);
      window.addEventListener('resize', () => {
        if (this.copy.dataset.detailsOpen === 'true') return;
        this.copyRestHeight = this.copy.offsetHeight;
        this.root.style.setProperty('--bsb-copy-rest-height', `${this.copyRestHeight}px`);
      }, { passive: true });
    }

    activate() {
      if (this.isActive || this.products.length < 2) return;
      this.isActive = true;
      this.root.dataset.active = 'true';
      if (this.overlay) this.overlay.hidden = true;
      if (this.closeButton) this.closeButton.hidden = false;
      this.resetWheel();
      this.alignToViewport();
      this.root.focus({ preventScroll: true });
      this.preloadAll();
    }

    // `restart` rewinds to the first fragrance, used when the visitor scrolls on past the last one.
    deactivate({ restoreFocus = false, restart = false } = {}) {
      if (!this.isActive) return;
      this.isActive = false;
      this.root.dataset.active = 'false';
      if (this.closeButton) this.closeButton.hidden = true;
      if (this.overlay) {
        this.overlay.hidden = false;
        if (restoreFocus) this.overlay.focus({ preventScroll: true });
      }
      this.touchStart = null;
      if (restart) this.goTo(0, 1);
    }

    getHeaderOffset() {
      const value = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));
      return this.root.dataset.fitHeader === 'true' && Number.isFinite(value) ? value : 0;
    }

    alignToViewport({ instant = false } = {}) {
      const top = this.root.getBoundingClientRect().top - this.getHeaderOffset();
      if (Math.abs(top) < 4) return;
      window.scrollTo({ top: window.scrollY + top, behavior: instant || this.motionQuery.matches ? 'auto' : 'smooth' });
    }

    // Native vertical panning is disabled while exploring, so the page is scrolled manually on exit.
    releaseScroll(direction) {
      const rect = this.root.getBoundingClientRect();
      const offset = this.getHeaderOffset();
      const distance = direction > 0 ? rect.bottom - offset : -Math.min(window.innerHeight - offset, rect.top + window.scrollY);
      window.scrollBy({ top: distance, behavior: this.motionQuery.matches ? 'auto' : 'smooth' });
    }

    scheduleResponsiveIngredientUpdate() {
      if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.updateResponsiveIngredientMetrics();
        // Content above the hero reflows with the width, so keep the active hero pinned below the header.
        if (this.isActive) this.alignToViewport({ instant: true });
      });
    }

    updateResponsiveIngredientMetrics() {
      const width = Math.max(0, window.innerWidth || this.root.clientWidth);
      const progress = Math.min(1, Math.max(0, (width - INGREDIENT_MIN_WIDTH) / (INGREDIENT_MAX_WIDTH - INGREDIENT_MIN_WIDTH)));
      this.ingredientScale = INGREDIENT_BASE_SCALE + (INGREDIENT_DESKTOP_SCALE - INGREDIENT_BASE_SCALE) * progress;
      const editorRotation = Number(this.root.dataset.ingredientEditorRotation) || 0;
      this.ingredientRotation = INGREDIENT_BASE_ROTATION + (INGREDIENT_DESKTOP_ROTATION - INGREDIENT_BASE_ROTATION) * progress + editorRotation;
      if (width <= 749) {
        this.ingredientScale *= 1.2;
        this.ingredientRotation = -45 + editorRotation;
      }
      this.ingredientScale *= this.ingredientScaleFactor;

      this.root.style.setProperty('--bsb-ingredient-scale', this.ingredientScale.toFixed(3));
      this.root.style.setProperty('--bsb-ingredient-rotation', `${this.ingredientRotation.toFixed(2)}deg`);

      if (this.gsap && !this.isAnimating) {
        this.gsap.set([this.currentIngredient, this.incomingIngredient], {
          scale: this.ingredientScale,
          rotation: this.ingredientRotation,
          transformOrigin: '50% 50%'
        });
      }
    }

    setDetailsOpen(open) {
      this.copy.dataset.detailsOpen = String(open);
      this.root.dataset.detailsOpen = String(open);
      this.detailsToggle?.setAttribute('aria-expanded', String(open));
      this.detailsToggle?.setAttribute('aria-label', open ? this.labels.hideDetails : this.labels.showDetails);
    }

    // The card height is tweened between its measured collapsed and expanded sizes so the details slide open.
    toggleDetails() {
      if (!this.copy) return;
      const open = this.copy.dataset.detailsOpen !== 'true';
      const details = this.copy.querySelector('.bsb-hero__details');
      const from = this.copy.offsetHeight;

      this.stopDetailsTween();
      this.setDetailsOpen(open);
      if (!this.gsap || this.motionQuery.matches || !details) return;

      const to = this.copy.offsetHeight;
      // Keep the details rendered while collapsing; the closed state is restored when the tween ends.
      if (!open) this.setDetailsOpen(true);

      this.detailsTween = this.gsap.timeline({
        onComplete: () => {
          if (!open) this.setDetailsOpen(false);
          this.stopDetailsTween();
        }
      });
      this.detailsTween
        .fromTo(this.copy, { height: from, overflow: 'hidden' }, { height: to, duration: DETAILS_DURATION, ease: 'power3.inOut' }, 0)
        .fromTo(details, { autoAlpha: open ? 0 : 1 }, { autoAlpha: open ? 1 : 0, duration: DETAILS_DURATION * (open ? 0.8 : 0.5), ease: 'power1.out' }, open ? DETAILS_DURATION * 0.2 : 0);
    }

    stopDetailsTween() {
      if (!this.detailsTween) return;
      this.detailsTween.kill();
      this.detailsTween = null;
      this.gsap.set(this.copy, { clearProps: 'height,overflow' });
      const details = this.copy.querySelector('.bsb-hero__details');
      if (details) this.gsap.set(details, { clearProps: 'opacity,visibility' });
    }

    handleKeydown(event) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLSelectElement) return;

      if (event.key === 'Escape' && this.isActive) {
        event.preventDefault();
        this.deactivate({ restoreFocus: true });
        return;
      }

      // At either end the keys fall through so the page keeps scrolling.
      const isDownKey = event.key === 'ArrowDown' || event.key === 'PageDown';
      if (isDownKey && this.currentIndex < this.lastIndex) {
        event.preventDefault();
        this.next();
      } else if (isDownKey && this.isActive) {
        this.deactivate({ restart: true });
      } else if ((event.key === 'ArrowUp' || event.key === 'PageUp') && this.currentIndex > 0) {
        event.preventDefault();
        this.previous();
      }
    }

    handleTouchStart(event) {
      if (!this.isActive || event.touches.length !== 1 || (this.copy?.dataset.detailsOpen === 'true' && this.copy.contains(event.target)) || event.target.closest('a, button, input, select, textarea')) {
        this.touchStart = null;
        return;
      }

      const touch = event.touches[0];
      this.touchStart = { id: touch.identifier, x: touch.clientX, y: touch.clientY };
    }

    handleTouchEnd(event) {
      if (!this.touchStart) return;

      const touch = [...event.changedTouches].find((item) => item.identifier === this.touchStart.id);
      if (!touch) return;

      const deltaX = touch.clientX - this.touchStart.x;
      const deltaY = touch.clientY - this.touchStart.y;
      this.touchStart = null;

      if (Math.abs(deltaY) < SWIPE_MIN_DISTANCE || Math.abs(deltaY) < Math.abs(deltaX) * SWIPE_AXIS_RATIO) return;

      const direction = deltaY < 0 ? 1 : -1;
      if (this.isAtBoundary(direction)) {
        this.deactivate({ restart: direction > 0 });
        this.releaseScroll(direction);
        return;
      }

      this.step(direction);
    }

    resetWheel() {
      Object.assign(this.wheel, { total: 0, peak: 0, lastEventAt: -Infinity, lastMagnitude: 0, locked: false });
    }

    handleWheel(event) {
      if (!this.isActive || this.products.length < 2) return;
      if (this.copy?.dataset.detailsOpen === 'true' && this.copy.contains(event.target)) return;
      if (event.ctrlKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

      const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.root.clientHeight : 1);
      const direction = Math.sign(delta);
      if (!direction) return;

      const wheel = this.wheel;
      const now = performance.now();
      const magnitude = Math.abs(delta);
      const isNewGesture = now - wheel.lastEventAt > WHEEL_GESTURE_GAP;
      wheel.lastEventAt = now;

      if (isNewGesture) {
        wheel.locked = false;
        wheel.total = 0;
        wheel.peak = 0;
      } else if (wheel.locked && this.isFreshPush(magnitude, now)) {
        wheel.locked = false;
        wheel.total = 0;
        wheel.peak = 0;
      }
      wheel.lastMagnitude = magnitude;
      wheel.peak = Math.max(wheel.peak, magnitude);

      // A new gesture past either end leaves the collection and lets the page scroll on.
      if (this.isAtBoundary(direction) && !wheel.locked) {
        event.preventDefault();
        if (isNewGesture) {
          this.deactivate({ restart: direction > 0 });
          // The browser does not scroll the page for the event that leaves the collection, so it is applied
          // by hand; the rest of the gesture scrolls natively.
          window.scrollBy({ top: delta, behavior: 'auto' });
        }
        return;
      }

      event.preventDefault();
      if (wheel.locked) return;

      if (Math.sign(wheel.total) !== direction) wheel.total = 0;
      wheel.total += delta;
      // The weak tail of a trackpad inertia stream never starts a slide on its own.
      if (Math.abs(wheel.total) < WHEEL_THRESHOLD || wheel.peak < WHEEL_MIN_PEAK) return;

      wheel.total = 0;
      wheel.locked = true;
      wheel.lastNavigationAt = now;
      wheel.smooth = magnitude;
      wheel.inertiaPeak = magnitude;
      wheel.inertiaTrough = magnitude;
      wheel.rising = 0;
      this.step(direction);
    }

    // While locked, inertia only decays. A new swipe during it shows up as a sustained rise of the smoothed
    // delta well above the lowest point reached since the slide changed; noisy devices never get there.
    isFreshPush(magnitude, now) {
      const wheel = this.wheel;
      const previous = wheel.smooth;
      wheel.smooth = previous * (1 - WHEEL_SMOOTHING) + magnitude * WHEEL_SMOOTHING;
      wheel.inertiaPeak = Math.max(wheel.inertiaPeak, wheel.smooth);
      if (wheel.smooth < wheel.inertiaTrough) {
        wheel.inertiaTrough = wheel.smooth;
        wheel.rising = 0;
      } else if (wheel.smooth > previous) {
        wheel.rising += 1;
      } else {
        wheel.rising = 0;
      }

      return now - wheel.lastNavigationAt > WHEEL_PUSH_MIN_INTERVAL
        && wheel.inertiaTrough < wheel.inertiaPeak * 0.5
        && wheel.smooth > Math.max(wheel.inertiaTrough * 3, WHEEL_MIN_PEAK * 2.5)
        && wheel.rising >= 4;
    }

    isAtBoundary(direction) {
      return direction > 0 ? this.currentIndex >= this.lastIndex : this.currentIndex <= 0;
    }

    step(direction) {
      if (direction > 0) this.next();
      else this.previous();
    }

    handleBlockSelect(event) {
      const block = event.target.closest?.('[data-bsb-product]');
      if (!block || !this.root.contains(block)) return;

      const targetIndex = [...this.root.querySelectorAll('[data-bsb-product]')].indexOf(block);
      if (targetIndex < 0 || targetIndex === this.currentIndex) return;
      this.goTo(targetIndex, targetIndex > this.currentIndex ? 1 : -1);
    }

    handleMotionPreference(event) {
      this.ambientTimeline?.kill();
      this.ambientTimeline = null;
      this.gsap?.set(this.ingredientMotionLayers, { rotation: 0 });
      if (event.matches) this.resetParallax();
      if (!event.matches) this.startAmbientMotion();
    }

    next() {
      if (this.currentIndex < this.lastIndex) this.goTo(this.currentIndex + 1, 1);
    }

    previous() {
      if (this.currentIndex > 0) this.goTo(this.currentIndex - 1, -1);
    }

    goTo(targetIndex, direction = 1) {
      if (targetIndex === this.currentIndex || !this.products[targetIndex]) return;

      // Commit the previous destination before starting an interrupting gesture.
      this.timeline?.progress(1);

      const product = this.products[targetIndex];
      this.currentIndex = targetIndex;
      this.isAnimating = true;
      this.root.setAttribute('aria-busy', 'true');

      if (!this.gsap || this.motionQuery.matches) {
        this.applyProductImmediate(product, targetIndex);
        this.unlock(targetIndex);
        return;
      }

      this.prepareIncoming(product, direction);
      this.runTransition(product, targetIndex, direction);
    }

    prepareIncoming(product, direction) {
      this.setBackground(this.incomingBackground, product);
      this.prepareImage(this.incomingIngredient, product.ingredientSrc, '');
      this.prepareImage(this.incomingBottle, product.bottleSrc, '');
      this.setSweepMask(product.bottleSrc);

      this.gsap.set(this.currentIngredient, {
        autoAlpha: this.currentIngredient?.hidden ? 0 : 1,
        scale: this.ingredientScale,
        rotation: this.ingredientRotation,
        xPercent: 0,
        yPercent: 0,
        transformOrigin: '50% 50%'
      });
      this.gsap.set(this.incomingIngredient, {
        autoAlpha: 0,
        scale: this.ingredientScale * 0.55,
        rotation: this.ingredientRotation,
        xPercent: 0,
        yPercent: direction * 6,
        transformOrigin: '50% 50%'
      });
      this.gsap.set(this.incomingBackground, { autoAlpha: 0 });
      this.gsap.set(this.currentBottle, { autoAlpha: 1 });
      this.gsap.set(this.incomingBottle, { autoAlpha: 0 });
      this.gsap.set(this.lightSweepBand, { autoAlpha: 0, xPercent: -160, skewX: -10 });
    }

    runTransition(product, targetIndex, direction) {
      this.timeline = this.gsap.timeline({
        defaults: { overwrite: 'auto' },
        onComplete: () => this.completeTransition(product, targetIndex)
      });
      this.timeline.timeScale(this.animationSpeed / TRANSITION_STRETCH);

      // Outgoing and incoming layers overlap so each slide settles in roughly half a second (before TRANSITION_STRETCH).
      this.timeline
        .to(this.copy, { autoAlpha: 0, y: direction * -10, duration: 0.14, ease: 'power2.in' }, 0)
        .to(this.currentIngredient, {
          autoAlpha: 0,
          scale: this.ingredientScale * 0.55,
          yPercent: direction * -6,
          duration: 0.3,
          ease: 'power2.in'
        }, 0)
        .to(this.incomingBackground, { autoAlpha: 1, duration: 0.38, ease: 'power1.inOut' }, 0)
        // The bottle stays put: the new one fades in on top of the old, so only the label appears to change.
        .to(this.incomingBottle, { autoAlpha: 1, duration: 0.3, ease: 'power1.inOut' }, 0.06)
        .call(() => this.updateCopy(product, targetIndex), [], 0.14)
        .to(this.incomingIngredient, {
          autoAlpha: 1,
          scale: this.ingredientScale,
          yPercent: 0,
          duration: 0.5,
          ease: 'expo.out'
        }, 0.12)
        .to(this.copy, { autoAlpha: 1, y: 0, duration: 0.3, ease: 'power3.out' }, 0.16)
        .to(this.lightSweepBand, { autoAlpha: 0.9, xPercent: 120, duration: 0.24, ease: 'power1.out' }, 0.2)
        .to(this.lightSweepBand, { autoAlpha: 0, xPercent: 390, duration: 0.26, ease: 'power2.in' }, 0.44);
    }

    completeTransition(product, targetIndex) {
      this.commitImage(this.currentIngredient, product.ingredientSrc, '');
      this.commitImage(this.currentBottle, product.bottleSrc, product.bottleAlt || product.title);
      this.setBackground(this.currentBackground, product);
      this.root.style.setProperty('--bsb-bg', product.background || '#ead9cd');

      this.gsap.set(this.currentIngredient, {
        autoAlpha: 1,
        scale: this.ingredientScale,
        rotation: this.ingredientRotation,
        xPercent: 0,
        yPercent: 0
      });
      this.gsap.set(this.currentBottle, { autoAlpha: 1 });
      this.gsap.set(this.incomingBackground, { autoAlpha: 0 });
      this.gsap.set(this.incomingIngredient, { autoAlpha: 0 });
      this.gsap.set(this.copy, { autoAlpha: 1, y: 0 });
      this.gsap.set(this.lightSweepBand, { autoAlpha: 0, xPercent: -160 });

      this.incomingIngredient.hidden = true;
      this.timeline = null;
      this.hideIncomingBottleWhenReady();
      this.unlock(targetIndex);
    }

    // Keep the incoming bottle on top until the current one has the new image painted, so it never blinks.
    hideIncomingBottleWhenReady() {
      const bottle = this.currentBottle;
      const hide = () => {
        if (this.timeline) return;
        this.gsap.set(this.incomingBottle, { autoAlpha: 0 });
        this.incomingBottle.hidden = true;
      };
      if (!bottle || bottle.complete) hide();
      else bottle.decode?.().then(hide, hide) ?? bottle.addEventListener('load', hide, { once: true });
    }

    applyProductImmediate(product, targetIndex) {
      this.root.style.setProperty('--bsb-bg', product.background || '#ead9cd');
      this.setBackground(this.currentBackground, product);
      this.commitImage(this.currentIngredient, product.ingredientSrc, '');
      this.commitImage(this.currentBottle, product.bottleSrc, product.bottleAlt || product.title);
      this.setSweepMask(product.bottleSrc);
      this.updateCopy(product, targetIndex);
      this.gsap?.set(this.currentIngredient, {
        autoAlpha: 1,
        scale: this.ingredientScale,
        rotation: this.ingredientRotation,
        xPercent: 0,
        yPercent: 0
      });
    }

    updateCopy(product, index) {
      if (this.title) this.title.textContent = product.title || '';
      if (this.tagline) this.tagline.textContent = product.tagline || '';
      if (this.description) this.description.textContent = product.description || '';
      if (this.profile) this.profile.textContent = product.profile || '';
      if (this.mood) this.mood.textContent = product.mood || '';
      if (this.moment) this.moment.textContent = product.moment || '';
      this.toggleEmpty(this.tagline, product.tagline);
      this.toggleEmpty(this.description, product.description);
      this.toggleEmpty(this.profile?.parentElement, product.profile);
      this.toggleEmpty(this.mood?.parentElement, product.mood);
      this.toggleEmpty(this.moment?.parentElement, product.moment);

      if (this.shopButton) {
        if (product.url) this.shopButton.setAttribute('href', product.url);
        else this.shopButton.removeAttribute('href');
        this.shopButton.hidden = !product.url;
      }

      if (this.copy) {
        this.stopDetailsTween();
        this.setDetailsOpen(false);
      }

      this.indicatorDots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === index);
        if (dotIndex === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this.indicator) {
        this.indicator.setAttribute('aria-label', this.labels.indicator.replace('[current]', index + 1).replace('[total]', this.products.length));
      }
      this.root.dispatchEvent(new CustomEvent('bsb:productchange', { detail: { index, product } }));
    }

    toggleEmpty(element, value) {
      if (element) element.hidden = !value;
    }

    prepareImage(image, source, alt) {
      if (!image) return;
      image.hidden = !source;
      if (!source) return;
      image.removeAttribute('srcset');
      image.src = source;
      image.alt = alt;
    }

    setBackground(layer, product) {
      if (!layer) return;
      layer.style.setProperty('--bsb-layer-bg', product.background || '#ead9cd');
      if (product.backgroundSrc) {
        layer.style.backgroundImage = `url(${JSON.stringify(product.backgroundSrc)})`;
        layer.style.backgroundSize = 'cover';
        layer.style.backgroundPosition = product.backgroundPosition || 'center';
      } else {
        layer.style.removeProperty('background-image');
      }
    }

    commitImage(image, source, alt) {
      if (!image) return;
      image.hidden = !source;
      if (!source) return;
      image.removeAttribute('srcset');
      image.src = source;
      image.alt = alt;
    }

    setSweepMask(source) {
      if (!this.lightSweep || !source) return;
      const mask = `url(${JSON.stringify(new URL(source, document.baseURI).href)})`;
      this.lightSweep.style.setProperty('--bsb-bottle-mask', mask);
      this.lightSweep.style.webkitMaskImage = mask;
      this.lightSweep.style.maskImage = mask;
    }

    startAmbientMotion() {
      if (!this.gsap || !this.ingredientMotionLayers.length || this.motionQuery.matches) return;

      this.gsap.set(this.ingredientMotionLayers, {
        rotation: 0,
        transformOrigin: '50% 50%'
      });

      this.ambientTimeline = this.gsap.timeline({ repeat: -1 });
      this.ambientTimeline
        .to(this.ingredientMotionLayers, { rotation: 7, duration: 18, ease: 'sine.inOut' })
        .to(this.ingredientMotionLayers, { rotation: -7, duration: 36, ease: 'sine.inOut' })
        .to(this.ingredientMotionLayers, { rotation: 0, duration: 18, ease: 'sine.inOut' });
    }

    // The background drifts against the cursor, which reads as depth behind the bottle and ingredients.
    setupParallax() {
      if (!this.gsap || !this.backgroundStack || !this.parallaxAmount) return;
      const tween = { duration: PARALLAX_DURATION, ease: 'power3.out' };
      this.parallaxX = this.gsap.quickTo(this.backgroundStack, 'x', tween);
      this.parallaxY = this.gsap.quickTo(this.backgroundStack, 'y', tween);

      const options = { signal: this.controller.signal };
      this.root.addEventListener('pointermove', (event) => this.handleParallax(event), options);
      this.root.addEventListener('pointerleave', () => this.resetParallax(), options);
    }

    handleParallax(event) {
      if (event.pointerType !== 'mouse' || !this.parallaxQuery.matches || this.motionQuery.matches) return;
      const rect = this.root.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const offsetX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const offsetY = ((event.clientY - rect.top) / rect.height) * 2 - 1;
      this.parallaxX(-offsetX * rect.width * this.parallaxAmount);
      this.parallaxY(-offsetY * rect.height * this.parallaxAmount);
    }

    resetParallax() {
      this.parallaxX?.(0);
      this.parallaxY?.(0);
    }

    unlock(targetIndex) {
      this.currentIndex = targetIndex;
      this.isAnimating = false;
      this.root.removeAttribute('aria-busy');
      this.preloadAdjacent(targetIndex);
    }

    preloadAdjacent(index) {
      [index - 1, index + 1, index + 2].forEach((productIndex) => this.preloadProduct(this.products[productIndex]));
    }

    // Fast browsing needs every slide decoded up front, so everything loads once the visitor opts in.
    preloadAll() {
      this.products.forEach((product) => this.preloadProduct(product));
    }

    preloadProduct(product) {
      if (!product) return;
      this.preloadSource(product.bottleSrc);
      this.preloadSource(product.ingredientSrc);
      this.preloadSource(product.backgroundSrc);
    }

    preloadSource(source) {
      if (!source || this.preloadCache.has(source)) return;
      const image = new Image();
      image.decoding = 'async';
      image.src = source;
      this.preloadCache.set(source, image);
    }

    destroy() {
      this.controller.abort();
      this.viewportObserver?.disconnect();
      this.copyObserver?.disconnect();
      this.timeline?.kill();
      this.ambientTimeline?.kill();
      this.detailsTween?.kill();
      if (this.backgroundStack) this.gsap?.killTweensOf(this.backgroundStack);
      if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
      this.preloadCache.clear();
      delete this.root.dataset.bsbHeroReady;
    }
  }

  const instances = new WeakMap();

  function mount(scope = document) {
    const roots = scope.matches?.(SELECTOR) ? [scope] : scope.querySelectorAll(SELECTOR);

    roots.forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new BSBProductHero(root));
    });
  }

  function unmount(scope) {
    const roots = scope.matches?.(SELECTOR) ? [scope] : scope.querySelectorAll(SELECTOR);

    roots.forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  } else {
    mount();
  }

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target));
})();
